import React, {useState, useEffect} from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import Fig1 from '../img/AISNIPS_Documentation_Fig1.png';
import Fig2 from '../img/AISNIPS_Documentation_Fig2.png';

export default function HowTo() {
const [ AWSVid, setAWSVid ] = useState(false);
const { user } = useAuthenticator((context) => [context.user]);
console.log(user)
const email = user['attributes']['email']
// The cognito:groups key has the value of an array containing all the groups of which the user has membership.
const group = user["signInUserSession"]["idToken"]['payload']['cognito:groups']
function checkUser(groupName){
  if (groupName === 'admin' || groupName === 'researcher'){ setAWSVid(true); }
}

  useEffect(() => {
    checkUser(group[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [AWSVid]);
  
  return (
    
    <div id="HowTo-Container">
      <div id="HowTo-Body"> Hello {email}, you are part of the group {group} </div>
      <div id="HowTo-Videos" style={{ display: "flex" }}>

        <div>
            <div style={{ textAlign: "center" }}><div><h2>AI-SNIPS: How To Use The Jobs Dashboard</h2></div>
        </div>
            
        <div style={{ position: "relative", paddingBottom: "56.25%" }}>
          <iframe style={{ width: "100%", height: "100%", position: "absolute", left: "0px", top: "0px", borderRadius: "10px" }}
            src="https://embed.app.guidde.com/playbooks/4Lu5Zct7sHNbBLKJTTN65E"
            title="AI-SNIPS: How To Use The Jobs Dashboard"
            frameborder="0"
            referrerpolicy="unsafe-url"
            allowfullscreen="true"
            allow="clipboard-write"
            sandbox="allow-popups allow-popups-to-escape-sandbox allow-scripts allow-forms allow-same-origin allow-presentation">
            
          </iframe>
        </div>
      </div>
    </div>

    <div id="HowToAPIDocs">
       <h2>Link to the web API Swagger Docs: </h2> 
       <a href='http://localhost:5000/api/'>http://localhost:5000/api/</a>
    </div>

    <div id='DocumentationBody' style={{ padding: "20px", lineHeight: "1.6" }}>

        <h1>AISNIPS Documentation</h1>
        <h2>How To Prepare Lead Sheet Files for The AISNIPS Platform</h2>
        
        <h3>Overview</h3>
        <p>The 2025 lead sheet Excel file that Dr K received recently is in a format incompatible with the AISNIPS platform. Below is a detailed guide for converting this lead sheet to an acceptable format.</p>
        <p>For context, here are some screenshots of the spreadsheet columns for which the AISNIPS platform was designed:</p>
        <figure>
          <img src={Fig1} alt="Figure 1: Expected Format" style={{ maxWidth: '100%', height: 'auto' }} />
          <figcaption>Figure 1: Expected Format</figcaption>
        </figure>
        <p>And here is an example of the format received:</p>
        <figure>
          <img src={Fig2} alt="Figure 2: Example of Received Format" style={{ maxWidth: '100%', height: 'auto' }} />
          <figcaption>Figure 2: Example of Received Format</figcaption>
        </figure>
        <p>Certain differences from the expected format are acceptable while others are not due to how the AISNIPS platform processes the files. Please follow the steps below to align your lead sheet with the expected format.</p>

        <h3>Step 1: Make sure each file has only one sheet</h3>
        <ol>
          <li>Open your Excel file.</li>
          <li>If it has more than one worksheet (tabs at the bottom), you need to turn it into one sheet per file:
            <ul>
              <li>Option A (one big file): Copy all the data from the extra sheets and paste them below each other in one sheet.</li>
              <li>Option B (multiple files): Save each sheet as its own Excel file.</li>
            </ul>
          </li>
          <li>Close and save.</li>
        </ol>
        <p><strong>Why?</strong> The system only reads one sheet per file when converting to CSV.</p>

        <h3>Step 2: Ensure the URL column is named exactly <code>url</code></h3>
        <ol>
          <li>Find the column that holds your web addresses (it might be called “Lead URL”, “URL”, or something else).</li>
          <li>Click the header cell (where it says “Lead URL” or “URL”), delete the old name, and type in <code>url</code> (all lowercase).</li>
          <li>Press Enter.</li>
        </ol>
        <p><strong>Why?</strong> The uploader expects a column called <code>url</code>—anything else will be ignored.</p>

        <h3>Step 3: Add three new columns</h3>
        <p>At the right end of your sheet, create three blank columns with these exact names (type each into the top cell of a new column):</p>
        <ul>
          <li><code>uid</code></li>
          <li><code>lead_index</code></li>
          <li><code>domain</code></li>
        </ul>

        <h3>Step 4: Rename “Product Search” to <code>search_term</code></h3>
        <ol>
          <li>Find the column currently titled “Product Search.”</li>
          <li>Click its header, delete “Product Search,” and type <code>search_term</code>.</li>
          <li>Press Enter.</li>
        </ol>

        <h3>Step 5: Fill in <code>lead_index</code> (0, 1, 2, …)</h3>
        <ol>
          <li>In the first data row under <code>lead_index</code>, type <code>0</code>.</li>
          <li>In the next row down, type <code>1</code>.</li>
          <li>Select both cells (0 and 1), then drag the little fill-handle (a small square at the bottom-right of the selection) down through all your rows—it will auto-increment the numbers.</li>
        </ol>

        <h3>Step 6: Build the <code>uid</code> values</h3>
        <p>Each UID combines your lead type (e.g. B2B) with that row’s index:</p>
        <ol>
          <li>Click the first cell under <code>uid</code>.</li>
          <li>Type a formula, for example if your lead type is B2B and your first data row is row 2:</li>
          <li><code>="B2B"&lead_index_cell</code>
            <br />– If <code>lead_index</code> is in column C, row 2, that would be:
            <br /><code>="B2B"&C2</code>
          </li>
          <li>Press Enter, then drag the fill-handle down to copy for all rows.</li>
          <li>The results will look like B2B0, B2B1, B2B2, etc.</li>
        </ol>

        <h3>Step 7: Extract the <code>domain</code> from each URL</h3>
        <ol>
          <li>Click the first cell under <code>domain</code> (e.g. D2).</li>
          <li>Enter this formula, assuming your <code>url</code> is in C2:</li>
          <li><code>=LEFT(C2, IFERROR(FIND("/", C2) - 1, LEN(C2)))</code></li>
          <li>Press Enter, then drag the fill-handle down to apply to every row.</li>
          <li>You’ll get just the domain part (for example, example.com).</li>
        </ol>

        <h3>Step 8: Replace all blank cells with <code>0</code></h3>
        <p>Empty cells can cause errors during upload. To fill them:</p>
        <ol>
          <li>Select your entire sheet (click the top-left corner or press Ctrl + A).</li>
          <li>Press Ctrl + H to open Find & Replace.</li>
          <li>Leave <strong>Find what</strong> blank.</li>
          <li>In <strong>Replace with</strong>, type <code>0</code> (zero).</li>
          <li>Click <strong>Options</strong> and check <strong>Match entire cell contents</strong>.</li>
          <li>Click <strong>Replace All</strong>, then close the dialog.</li>
        </ol>

        <h3>Step 9: Save (export) as CSV UTF-8</h3>
        <ol>
          <li>Go to <strong>File → Save As</strong>.</li>
          <li>Choose <strong>Browse…</strong> and pick where to save.</li>
          <li>In “Save as type,” select <strong>CSV UTF-8 (Comma delimited)</strong>.</li>
          <li>Click <strong>Save</strong>.</li>
        </ol>
        <p><strong>Why UTF-8?</strong> It ensures all characters—especially non-English ones—upload correctly.</p>

        <h3>Troubleshooting: “Duplicate columns” error</h3>
        <p>If the upload page complains about duplicate columns even though your Excel looks fine:</p>
        <ol>
          <li>Open your Excel sheet, select all cells (Ctrl + A), then copy (Ctrl + C).</li>
          <li>Open a brand-new Excel file and paste (Ctrl + V).</li>
          <li>Repeat Step 9 to save that new file as CSV UTF-8.</li>
          <li>Upload the new CSV.</li>
        </ol>

        <hr style={{ margin: "40px 0" }} />

        <h2>Data Dictionary Purpose</h2>
        <p>This document is intended to inform current & potential AI-SNIPS stakeholders: how to prepare a leadsheet to be uploaded to the AI-SNIPS website for scraping & analysis.</p>
        <p>This is only a summary of the data dictionary. Please refer to the Data Dictionary sheet for complete details.</p>
        <p>It also explains the input/output AI-SNIPS data structures, as well as best practices to follow so that the resultant Illicit, Substandard, and Falsified Medical Products (ISFMP) network is of high quality and suitable for investigations.</p>

        <h2>AI-SNIPS: Input</h2>

        <h3>Case/Row/Lead</h3>

        <h4>Required (3 attributes)</h4>
        <ul>
          <li>The URL suspected of ISFMP activity (column name: url)</li>
          <li>Lead type of case. See Lead Types sheet for a complete listing of options (column name: lead_type)</li>
          <li>UID (unique identifier). A unique label on the node that will be shown in the network as the label (column name: uid)</li>
        </ul>

        <h4>Optional (47+ attributes)</h4>
        <p>Additional validated information about the case/domain which can be used as ground-truth.</p>
        <ul>
          <li>If multiple URLs per case are available, and they are known apriori to be related to each other, please add them as separate cases/rows and add a ground_truth_cluster_id column to link the cases with a common integer.</li>
          <li>In this context, validated means it has been reviewed for accuracy by the stakeholder and is correct to the best of their knowledge.</li>
          <li>Examples include keywords, case notes, investigation outcomes, and test purchase details.</li>
          <li>We highly encourage adding optional attributes to cases when available.</li>
        </ul>

        <h3>Remarks</h3>
        <ul>
          <li><strong>One Case represents:</strong>
            <ul>
              <li>One node in the output network</li>
              <li>One row in the stakeholder-provided lead sheet</li>
            </ul>
          </li>
          <li><strong>One Leadsheet consists of:</strong>
            <ul>
              <li>Rows: cases</li>
              <li>Columns: attributes</li>
            </ul>
          </li>
          <li>Multiple of the same attribute type per case/row is supported for most attributes.</li>
          <li>For all optional case attributes, incomplete/missing data is allowed.</li>
          <li>Aggregate of all ISFMP case attributes encountered to date (across all stakeholders)</li>
          <li>Most not currently used to link sellers</li>
          <li>New attributes can be added upon stakeholder request</li>
        </ul>

        <h2>AI-SNIPS: Output</h2>
        <h3>Clustered Cases</h3>
        <p>Cases that had no similarities with other cases (i.e., no edges) are discarded.</p>
      </div>
  </div>
  )
}